/**
 * ADR-0192 — pure logic behind the file step's upload wizard and Columns
 * table (`components/operationInspector/OperationInspectorFileProvider.tsx`).
 * Kept here so it is unit-testable (console tests are node:test on plain TS),
 * beside `columnDomain.ts` whose vocabulary and toast copy it reuses.
 *
 *   - wizard rows default from the sniff's REAL resolution (spec §3 F2 closed)
 *   - the Type cell reads the step's declaration, or "String → Number" when an
 *     unlocked step's file reads a column differently
 *   - the confirm prompt lists every change in words, plus the re-initialize
 *     line when a keyed stored dataset sits downstream
 *   - the two inline refusal messages (a value that does not fit; a locked step)
 */

import {
  COLUMN_DOMAIN_OPTIONS,
  isColumnDomain,
  typeValidationMessage,
  type ColumnDomain,
  type TypeValidationFailures,
} from "./columnDomain.js";
import type {
  ColumnResolutionInput,
  DownstreamKeyedPersist,
  ResolutionMode,
  SniffColumn,
  SniffResult,
  StructuralDriftPreview,
} from "./pdoUploadTypes.js";

export type WizardRow = {
  source_header: string;
  normalized_header: string;
  /** Sniff vocabulary — informational; never shown raw (spec §5.1). */
  inferred_type: string;
  /** existing | new | skip — defaulted from the sniff's resolution, then the person's choice. */
  user_resolution: "existing" | "new" | "skip";
  canonical_name: string | null;
  /** First upload only: the person's seed for this column (null = the sniff's proposal). */
  type_override: ColumnDomain | null;
  original_resolution_mode: ResolutionMode;
  /** The step's declared domain for the column this header resolves to; null on a first upload / unresolved. */
  declared_type: ColumnDomain | null;
  /** False when every cell in the file's column is blank — such a column agrees with any declaration. */
  has_values: boolean;
};

/** The sniff's proposal for a column, in the domain vocabulary (the hub's seed rule). */
export function seedDomain(inferredType: string): ColumnDomain {
  switch (inferredType) {
    case "integer":
    case "numeric":
    case "number":
      return "number";
    case "date":
    case "timestamp":
      return "date";
    case "boolean":
      return "boolean";
    default:
      return "string";
  }
}

export function domainLabel(domain: string | null | undefined): string {
  const opt = COLUMN_DOMAIN_OPTIONS.find((o) => o.value === domain);
  return opt ? opt.label : domain ? domain : "—";
}

/**
 * Default every row from the resolution the sniff actually computed:
 * exact / sticky / suggested → Map with the canonical prefilled; unresolved →
 * New with the normalized header and no `existing` claim; first upload →
 * New with the seed shown.
 */
export function rowsFromSniff(sniff: Pick<SniffResult, "columns">): WizardRow[] {
  return sniff.columns.map((c: SniffColumn): WizardRow => {
    const mode = c.resolution.mode;
    const resolved = mode === "exact_match" || mode === "sticky_mapping" || mode === "suggested";
    return {
      source_header: c.source_header,
      normalized_header: c.normalized_header,
      inferred_type: c.inferred_type,
      user_resolution: resolved ? "existing" : "new",
      canonical_name: resolved ? c.resolution.canonical_name : (c.resolution.canonical_name ?? c.normalized_header),
      type_override: null,
      original_resolution_mode: mode,
      declared_type: isColumnDomain(c.declared_type) ? c.declared_type : null,
      has_values: c.has_values !== false,
    };
  });
}

/** The request body rows. `type_override` travels only on a first upload (the hub refuses it otherwise). */
export function rowsToColumnResolutions(rows: WizardRow[], isFirstUpload: boolean): ColumnResolutionInput[] {
  return rows.map((r) => ({
    source_header: r.source_header,
    resolution: r.user_resolution,
    canonical_name: r.user_resolution === "skip" ? null : r.canonical_name,
    type_override: isFirstUpload ? r.type_override : null,
  }));
}

export type RowTypeCell =
  | { kind: "seed"; domain: ColumnDomain }
  | { kind: "declared"; domain: ColumnDomain }
  | { kind: "change"; from: ColumnDomain; to: ColumnDomain }
  | { kind: "new"; domain: ColumnDomain };

/**
 * What the wizard's Type cell says for a row (spec §5.1). On a first upload
 * the seed (editable, `kind: "seed"`); on later uploads the declaration; on
 * an UNLOCKED step whose file reads the column differently, both — the change
 * the confirm prompt will name. A new column on a later upload shows its seed.
 */
export function rowTypeCell(row: WizardRow, ctx: { isFirstUpload: boolean; schemaLocked: boolean }): RowTypeCell {
  const seed = row.type_override ?? seedDomain(row.inferred_type);
  if (ctx.isFirstUpload) return { kind: "seed", domain: seed };
  if (row.user_resolution !== "existing" || row.declared_type === null) return { kind: "new", domain: seedDomain(row.inferred_type) };
  const fileDomain = seedDomain(row.inferred_type);
  if (!ctx.schemaLocked && row.has_values && fileDomain !== row.declared_type) {
    return { kind: "change", from: row.declared_type, to: fileDomain };
  }
  return { kind: "declared", domain: row.declared_type };
}

export function rowTypeText(cell: RowTypeCell): string {
  switch (cell.kind) {
    case "change":
      return `${domainLabel(cell.from)} → ${domainLabel(cell.to)}`;
    default:
      return domainLabel(cell.domain);
  }
}

/**
 * The confirm prompt's lines: every change in words, then the re-initialize
 * line when a keyed stored dataset sits downstream (spec §5.1).
 */
export function driftSentences(
  drift: StructuralDriftPreview | null,
  downstream: DownstreamKeyedPersist[] | null | undefined
): string[] {
  const lines: string[] = [];
  for (const c of drift?.new_columns ?? []) lines.push(`adds «${c.canonical_name}» (${domainLabel(c.type)})`);
  for (const c of drift?.removed_columns ?? []) lines.push(`drops «${c.canonical_name}»`);
  for (const c of drift?.type_changes ?? []) lines.push(`changes «${c.canonical_name}» from ${domainLabel(c.from)} to ${domainLabel(c.to)}`);
  for (const d of downstream ?? []) lines.push(`«${d.pdo_name}» will need to be re-initialized after this upload.`);
  return lines;
}

/**
 * `422 type_validation_failed` on an upload, rendered inline in the wizard
 * (a blocking issue near the field, never a toast). The first sentence is
 * `typeValidationMessage`'s without its "Kept as-is." tail; the second is
 * chosen by the lock.
 */
export function typeValidationInlineMessage(
  failures: TypeValidationFailures,
  typesByColumn: Record<string, string | undefined>,
  schemaLocked: boolean
): string {
  const [column] = Object.keys(failures ?? {});
  const attempted = column ? typesByColumn[column] : undefined;
  const first = typeValidationMessage(failures, isColumnDomain(attempted) ? attempted : "string").replace(/\s*Kept as-is\.$/, "");
  const second = schemaLocked
    ? "Fix the file, or unlock the step to change the column's type, then upload again."
    : "Fix the file, or change the column's type in Columns, then upload again.";
  return `${first} ${second}`;
}

export const SCHEMA_LOCKED_UPLOAD_MESSAGE = "This step's schema is locked. Unlock it to accept a file with different columns.";
export const UNLOCK_TO_RETYPE_HELP = "Unlock the step to change a column type.";
export const NO_COLUMNS_YET = "No columns yet — upload a file.";

/** The history row's Columns cell: count + a `name: Type` title. */
export function columnsSummary(schema: { columns: Array<{ name: string; type: string }> } | null | undefined): { text: string; title: string } {
  const cols = schema?.columns ?? [];
  return {
    text: `${cols.length} column${cols.length === 1 ? "" : "s"}`,
    title: cols.map((c) => `${c.name}: ${domainLabel(c.type)}`).join("\n"),
  };
}
